import React, { useEffect, useMemo, useRef, useState } from "react";
import { Users, User, Package, Truck, Search } from "react-feather";
import DateRangePicker, { useDatePresetRange } from "../../../../components/common/DateRangePicker";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import useEmployeePhotoCache from "../../../masters/employees/useEmployeePhotoCache";
import { useLazyGetOneStopReportQuery } from "../oneStopApi";
import { buildOneStopTree, buildOneStopKpis } from "../oneStopGrouping";
import OneStopIdentity, { OneStopKpis } from "../components/OneStopIdentity";
import OneStopMasterPicker from "../components/OneStopMasterPicker";
import OneStopTree from "../components/OneStopTree";
import UserActivityPanel from "../components/UserActivityPanel";

const MASTER_TYPES = [
  { id: "CUSTOMER", label: "Customer", sub: "branch + site", Icon: Users },
  { id: "EMPLOYEE", label: "Employee", sub: "+ user activity", Icon: User },
  { id: "PRODUCT", label: "Product", sub: "quantities", Icon: Package },
  { id: "VEHICLE", label: "Vehicle", sub: "global reg no.", Icon: Truck },
];

/**
 * One Stop - pick one master record and a period, get every module's
 * transactions for it, grouped Branch -> Site -> Module -> Transaction type
 * with drill-down into the screens that already exist.
 *
 * Fires on an explicit Search (lazy query), not on every filter keystroke: this
 * is a wide cross-module read, so it should run when the user says so.
 */
const OneStopPage = () => {
  const [masterType, setMasterType] = useState("CUSTOMER");
  const [selection, setSelection] = useState(null);
  const [productBranchId, setProductBranchId] = useState(null);
  const [includeVoided, setIncludeVoided] = useState(false);
  const [includeNameMatches, setIncludeNameMatches] = useState(true);

  const dateRange = useDatePresetRange("THIS_YEAR");
  const { selectedBranchId } = useBranchFilter();

  const [runReport, { data, isFetching, error, isUninitialized }] = useLazyGetOneStopReportQuery();

  const onPickMasterType = (id) => {
    setMasterType(id);
    setSelection(null);
    setProductBranchId(null);
  };

  // The product picker searches one branch: the header branch when one is picked,
  // otherwise the branch chosen in the picker's own select (shown only under "All").
  const effectiveProductBranchId = selectedBranchId ?? productBranchId;

  const canSearch = !!selection?.query;

  // The header branch drives the report (2026-10-02, "many pages are still not
  // triggering the global branch selection, like one stop"): once a report is on
  // screen, switching the header branch re-runs it for the new branch instead of
  // leaving the old branch's figures up. Before the first Search nothing fires.
  // A customer or product is itself a branch record: when the new branch is not
  // the picked one's branch, the pick is cleared and the old result hidden
  // (`isStale`) rather than re-running a report that cannot match.
  const lastSearchedBranch = useRef(undefined);
  const [isStale, setIsStale] = useState(false);
  useEffect(() => {
    if (lastSearchedBranch.current === undefined || lastSearchedBranch.current === selectedBranchId) return;
    const previousBranch = lastSearchedBranch.current;
    lastSearchedBranch.current = selectedBranchId;
    const pickedBranch =
      masterType === "CUSTOMER"
        ? selection?.customer?.branchId
        : masterType === "PRODUCT"
          ? previousBranch ?? productBranchId
          : masterType === "VEHICLE"
            ? selection?.vehicle?.branchId
            : null;
    if (selectedBranchId != null && pickedBranch != null && pickedBranch !== selectedBranchId) {
      setSelection(null);
      setIsStale(true);
      return;
    }
    search();
  }, [selectedBranchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = () => {
    if (!canSearch) return;
    lastSearchedBranch.current = selectedBranchId;
    setIsStale(false);
    runReport({
      masterType,
      ...selection.query,
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
      ...(selectedBranchId ? { branchId: selectedBranchId } : {}),
      includeVoided,
      includeNameMatches,
    });
  };

  // Stable identity for the row array: `data?.rows || []` would hand a brand
  // new array to every memo and to useEmployeePhotoCache on each render, which
  // makes that hook's effect re-loop every render for no reason.
  const rows = useMemo(() => data?.rows || [], [data]);
  const identity = data?.identity || null;
  const activity = useMemo(() => data?.userActivity || [], [data]);

  const { tree, money: isMoney } = useMemo(
    () => buildOneStopTree(rows, identity?.masterType || masterType),
    [rows, identity, masterType]
  );
  const kpis = useMemo(
    () => buildOneStopKpis(rows, identity?.masterType || masterType),
    [rows, identity, masterType]
  );

  // One blob request per employee however many rows repeat them - the same
  // shared cache Guard Lookup and the Attendance Dashboard use. Covers both the
  // counterparty avatars in the tree and the employee's own photo up top.
  const photoRows = useMemo(() => {
    const list = rows
      .filter((r) => r.counterpartyHasPhoto && r.counterpartyEmployeeRecordGuid)
      .map((r) => ({ employeeRecordGuid: r.counterpartyEmployeeRecordGuid }));
    if (identity?.masterType === "EMPLOYEE" && identity.hasPhoto && identity.employeeRecordGuid) {
      list.push({ employeeRecordGuid: identity.employeeRecordGuid });
    }
    return list;
  }, [rows, identity]);
  const photoUrls = useEmployeePhotoCache(photoRows);

  const apiMessage = error?.data?.message || error?.error || null;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>One Stop</h4>
              <h6>
                One master record, every module - grouped by branch and site, with drill-down to the
                source document
              </h6>
            </div>
          </div>
        </div>

        {/* ---------- criteria ---------- */}
        <div className="card mb-3">
          <div className="card-body">
            <label className="form-label text-uppercase fw-semibold text-muted" style={{ fontSize: 10.5, letterSpacing: ".08em" }}>
              Master record
            </label>
            <div className="row g-2 mb-3">
              {MASTER_TYPES.map((t) => {
                const active = masterType === t.id;
                return (
                  <div className="col-6 col-lg-3" key={t.id}>
                    <button
                      type="button"
                      className={`btn w-100 text-start d-flex align-items-center gap-2 ${
                        active ? "btn-outline-primary border-2 fw-semibold" : "btn-outline-secondary"
                      }`}
                      aria-pressed={active}
                      onClick={() => onPickMasterType(t.id)}
                    >
                      <t.Icon size={16} className="flex-shrink-0" />
                      <span className="min-w-0">
                        <span className="d-block">{t.label}</span>
                        <span className="d-block text-muted fw-normal" style={{ fontSize: 11 }}>
                          {t.sub}
                        </span>
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-4">
                <label className="form-label text-uppercase fw-semibold text-muted" style={{ fontSize: 10.5, letterSpacing: ".08em" }}>
                  {MASTER_TYPES.find((t) => t.id === masterType)?.label}
                </label>
                <OneStopMasterPicker
                  masterType={masterType}
                  selection={selection}
                  onSelect={setSelection}
                  productBranchId={effectiveProductBranchId}
                  setProductBranchId={setProductBranchId}
                  branchId={selectedBranchId}
                />
              </div>

              <div className="col-12 col-lg-5">
                <label className="form-label text-uppercase fw-semibold text-muted" style={{ fontSize: 10.5, letterSpacing: ".08em" }}>
                  Period
                </label>
                <DateRangePicker {...dateRange} />
              </div>

              <div className="col-12 col-lg-3">
                <div className="form-check mb-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="onestop-voided"
                    checked={includeVoided}
                    onChange={(e) => setIncludeVoided(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="onestop-voided" style={{ fontSize: 12.5 }}>
                    Include voided / deleted
                  </label>
                </div>
                {masterType === "CUSTOMER" && (
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="onestop-namematch"
                      checked={includeNameMatches}
                      onChange={(e) => setIncludeNameMatches(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="onestop-namematch" style={{ fontSize: 12.5 }}>
                      Match unconfirmed enquiries by name
                    </label>
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                  disabled={!canSearch || isFetching}
                  onClick={search}
                >
                  <Search size={15} />
                  {isFetching ? "Searching..." : "Search"}
                </button>
              </div>
            </div>

            {masterType === "CUSTOMER" && includeNameMatches && (
              <div className="text-muted mt-2" style={{ fontSize: 11.5 }}>
                A sales enquiry only gets a customer link when it is confirmed - before that the
                customer is free text. Name-matched rows are labelled &ldquo;matched on name
                only&rdquo; so they are never mistaken for a key-joined record.
              </div>
            )}
          </div>
        </div>

        {/* ---------- results ---------- */}
        {apiMessage && (
          <div className="alert alert-danger" role="alert">
            {typeof apiMessage === "string" ? apiMessage : "Could not load the report."}
          </div>
        )}

        {(isUninitialized || isStale) && !isFetching && (
          <div className="card">
            <div className="card-body text-center text-muted py-5">
              Pick a {MASTER_TYPES.find((t) => t.id === masterType)?.label.toLowerCase()} and a
              period, then press Search.
            </div>
          </div>
        )}

        {!isUninitialized && !isStale && !isFetching && identity && (
          <>
            <OneStopIdentity
              identity={identity}
              photoUrl={identity.employeeRecordGuid ? photoUrls?.[identity.employeeRecordGuid] : null}
            />
            <OneStopKpis kpis={kpis} masterType={identity.masterType} />

            {rows.length === 0 ? (
              <div className="card">
                <div className="card-body text-center text-muted py-5">
                  No transactions in any module for this record between {dateRange.dateFrom} and{" "}
                  {dateRange.dateTo}.
                </div>
              </div>
            ) : (
              <OneStopTree tree={tree} isMoney={isMoney} photoUrls={photoUrls} />
            )}

            <UserActivityPanel identity={identity} activity={activity} />
          </>
        )}
      </div>
    </div>
  );
};

export default OneStopPage;
